/**
 * useBackendBootstrap — backend runtime + persistent slice bootstrap logic.
 * Extracted from App.tsx — pure structural refactor.
 */

import { startTransition, useEffect, useState } from "react";

import type {
  AdminAuditResponse,
  DrawingsResponse,
  WorkspacesResponse,
} from "../backend/backendApi";
import {
  createBackendClient,
  resolveBackendApiBaseUrl,
} from "../backend/backendApi";
import { buildBackendAuthPresentation } from "../auth/authPresentation";
import type { BackendRuntimeState } from "../components/EditorHeader";
import type { BackendPersistentSliceState } from "../components/StatusStrips";
import {
  buildPersistentSliceErrorMessage,
  isExpectedAuthBoundaryError,
} from "../helpers/backendHelpers";

type PersistentSliceResult = PromiseSettledResult<
  WorkspacesResponse | DrawingsResponse | AdminAuditResponse
>;

export function useBackendBootstrap(
  hydrateSessionShell: (input: {
    bootstrapAbsoluteAdminEmail: string | null;
    users: Array<unknown>;
    preferredUserEmail: string | null;
  }) => void,
) {
  const [backendRuntime, setBackendRuntime] = useState<BackendRuntimeState>({
    status: "loading",
    apiBaseUrl: resolveBackendApiBaseUrl(),
  });
  const [backendPersistentSlice, setBackendPersistentSlice] =
    useState<BackendPersistentSliceState>({
      status: "loading",
      workspacesResponse: null,
      drawingsResponse: null,
      auditResponse: null,
    });

  useEffect(() => {
    let cancelled = false;
    const apiBaseUrl = resolveBackendApiBaseUrl();
    const client = createBackendClient({ baseUrl: apiBaseUrl });

    setBackendRuntime({
      status: "loading",
      apiBaseUrl,
      ...buildBackendAuthPresentation({
        status: "loading",
        apiBaseUrl,
      }),
    });
    setBackendPersistentSlice({
      status: "loading",
      workspacesResponse: null,
      drawingsResponse: null,
      auditResponse: null,
    });

    async function loadBackendShell() {
      try {
        const [bootstrapConfig, sessionShell] = await Promise.all([
          client.getBootstrapConfig(),
          client.getSessionShell(),
        ]);

        if (cancelled) {
          return;
        }

        const isAdminLikeSession =
          sessionShell.data.currentUser?.role === "admin" ||
          sessionShell.data.currentUser?.role === "absolute_admin";

        const adminUsers = isAdminLikeSession
          ? await client.getAdminUsers().catch(() => null)
          : null;

        startTransition(() => {
          hydrateSessionShell({
            bootstrapAbsoluteAdminEmail:
              sessionShell.data.bootstrapAbsoluteAdminEmail,
            users: adminUsers?.data ?? [],
            preferredUserEmail: sessionShell.data.currentUser?.email ?? null,
          });
        });

        setBackendRuntime({
          status: "ready",
          apiBaseUrl,
          serverAlias: sessionShell.data.serverAlias,
          authMode: sessionShell.data.authMode,
          deploymentMode: sessionShell.data.deploymentMode,
          ...buildBackendAuthPresentation({
            status: "ready",
            apiBaseUrl,
            bootstrapConfig,
            sessionShell,
          }),
          adminUserCount: adminUsers?.meta.total,
        });

        if (!sessionShell.data.authenticated) {
          setBackendPersistentSlice({
            status: "ready",
            workspacesResponse: null,
            drawingsResponse: null,
            auditResponse: null,
          });
          return;
        }

        void loadBackendPersistentSlice(client, apiBaseUrl, () => cancelled, {
          loadAudit: isAdminLikeSession,
        });
      } catch (error) {
        if (cancelled) {
          return;
        }

        setBackendRuntime({
          status: "error",
          apiBaseUrl,
          ...buildBackendAuthPresentation({
            status: "error",
            apiBaseUrl,
            errorMessage:
              error instanceof Error ? error.message : String(error),
          }),
          errorMessage: error instanceof Error ? error.message : String(error),
        });
        setBackendPersistentSlice({
          status: "error",
          workspacesResponse: null,
          drawingsResponse: null,
          auditResponse: null,
          errorMessage: error instanceof Error ? error.message : String(error),
        });
      }
    }

    void loadBackendShell();

    return () => {
      cancelled = true;
    };
  }, [hydrateSessionShell]);

  async function loadBackendPersistentSlice(
    client: ReturnType<typeof createBackendClient>,
    apiBaseUrl: string,
    isCancelled: () => boolean,
    options: {
      loadAudit: boolean;
    },
  ) {
    const [workspacesResult, drawingsResult] = await Promise.allSettled([
      client.getWorkspaces(),
      client.getDrawings(),
    ]);

    if (isCancelled()) {
      return;
    }

    const auditResult = options.loadAudit
      ? await client.getAdminAudit({ limit: 5 }).then(
          (value) => ({ status: "fulfilled" as const, value }),
          (reason) => ({ status: "rejected" as const, reason }),
        )
      : null;

    const settledResults: Array<PersistentSliceResult | null> = [
      workspacesResult,
      drawingsResult,
      auditResult,
    ];
    const unexpectedFailures = settledResults.filter(
      (result): result is PromiseRejectedResult =>
        result !== null &&
        result.status === "rejected" &&
        !isExpectedAuthBoundaryError(result.reason),
    );
    const successCount = settledResults.filter(
      (
        result,
      ): result is Exclude<PersistentSliceResult, PromiseRejectedResult> =>
        result !== null && result.status === "fulfilled",
    ).length;
    const shouldTreatAsReady = unexpectedFailures.length === 0;

    setBackendPersistentSlice({
      status: shouldTreatAsReady
        ? "ready"
        : successCount > 0
          ? "partial"
          : "error",
      workspacesResponse:
        workspacesResult.status === "fulfilled" ? workspacesResult.value : null,
      drawingsResponse:
        drawingsResult.status === "fulfilled" ? drawingsResult.value : null,
      auditResponse:
        auditResult?.status === "fulfilled" ? auditResult.value : null,
      errorMessage: buildPersistentSliceErrorMessage(apiBaseUrl, [
        workspacesResult,
        drawingsResult,
        auditResult,
      ]),
    });
  }

  return { backendRuntime, backendPersistentSlice };
}
